"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground text-sm font-default font-semibold tracking-widest uppercase hover:text-primary transition-colors px-0 py-1 bg-transparent hover:bg-transparent focus:bg-transparent">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6 pt-12">
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col px-6 pt-16">
      {menuItems.map((item, i) => <div data-index={i} key={item.id} className="border-b border-border/50 py-5">
          <SheetClose asChild>
            <Link to={item.href || "#"} onClick={onClose} className="text-foreground font-default text-3xl font-bold tracking-tighter hover:text-primary transition-colors block uppercase">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const phoneNumber = "817-291-3051";
  const phoneHref = "tel:+18172913051";
  const gioNumber = "817-420-0631";
  const gioHref = "tel:+18174200631";
  return <section data-section-id="985" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "backdrop-blur-lg shadow-md border-b border-primary/40" : "border-b border-border"}`}>
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        <div className="flex h-20 items-center justify-between gap-6">
          {}
          <div className="flex items-center shrink-0">
            <Link to="/HomePage" className="flex items-center" aria-label="JOE&GIO HAUL IT ALL! Home">
              <WvcLogo className="!w-[70px] !h-[69px] !m-0 [&_svg]:!w-[70px] [&_svg]:!h-[69px] [&_svg]:!m-0" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="46" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <span className="font-mono uppercase tracking-[0.2em] text-primary whitespace-nowrap font-bold !text-2xl" aria-label="Fast Hauling">Joe&amp;Gio's Haul it all</span>
            <Link to={phoneHref} aria-label={`Call Joe ${phoneNumber}`}>
              <Button className="relative rounded-md bg-primary hover:bg-primary/90 text-foreground px-6 h-12 font-default font-bold tracking-wide shadow-lg border-2 border-foreground">
                <span className="absolute inset-0 rounded-md ring-2 ring-primary/20 animate-pulse pointer-events-none" aria-hidden="true" />
                <Phone className="size-5" />
                <span>CALL JOE: {phoneNumber}</span>
              </Button>
            </Link>
            <Link to={gioHref} aria-label={`Call Gio ${gioNumber}`}>
              <Button className="relative rounded-md bg-primary hover:bg-primary/90 text-foreground px-6 h-12 font-default font-bold tracking-wide shadow-lg border-2 border-foreground">
                <span className="absolute inset-0 rounded-md ring-2 ring-primary/20 animate-pulse pointer-events-none" aria-hidden="true" />
                <Phone className="size-5" />
                <span>CALL GIO: {gioNumber}</span>
              </Button>
            </Link>
          </div>

          {}
          <div className="flex lg:hidden items-center gap-3">
            <Link to={phoneHref} aria-label={`Call Joe ${phoneNumber}`}>
              <Button size="icon" className="rounded-md bg-primary hover:bg-primary/90 text-foreground h-10 w-10 shadow-md border border-foreground">
                <Phone className="size-4" />
              </Button>
            </Link>
            <Link to={gioHref} aria-label={`Call Gio ${gioNumber}`}>
              <Button size="icon" className="rounded-md bg-primary hover:bg-primary/90 text-foreground h-10 w-10 shadow-md border border-foreground">
                <Phone className="size-4" />
              </Button>
            </Link>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent h-10 w-10 rounded-md" aria-label="Open menu">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background p-0 flex flex-col border-l border-border">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

                <div className="flex items-center justify-between px-6 h-20 border-b border-border">
                  <Link to="/HomePage" onClick={() => setMobileOpen(false)} aria-label="JOE&GIO HAUL IT ALL! Home">
                    <WvcLogo className="h-8 w-auto" />
                  </Link>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary font-bold">
                    [ 24/7 ]
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <MenuProvider menu_id="46" className="mobile-menu">
                    <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-6 pb-8 pt-6 border-t border-border bg-card">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary mb-4 text-center font-bold">
                    [ READY TO HAUL ]
                  </p>
                  <SheetClose asChild>
                    <Link to={phoneHref} onClick={() => setMobileOpen(false)} className="block">
                      <Button className="relative w-full rounded-md bg-primary hover:bg-primary/90 text-foreground h-14 font-default font-bold tracking-wide shadow-xl text-lg border-2 border-foreground">
                        <span className="absolute inset-0 rounded-md ring-2 ring-primary/30 animate-pulse pointer-events-none" aria-hidden="true" />
                        <Phone className="size-6" />
                        <span>CALL JOE NOW</span>
                      </Button>
                    </Link>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}